<script setup lang="ts">
import { computed } from 'vue';
import type { Status } from '@/interfaces/Dashboard';

const props = defineProps<{
  status: Status;
  title?: number | string | null;
  subttitle?: number | string | null;
  badgeText?: string | null;
  labelText?: string | null;
  ratio?: { value: number; total: number };
  progressHeight?: number | string;
  progressrounded?: boolean; 
}>();

const dangerStyles = {
  border: 'tw-border-connx-danger',
  borderLeft: 'tw-border-l-connx-danger',
  text: 'tw-text-connx-danger',
  bg: 'tw-bg-connx-danger',
};

const statusClasses = {
  success: {
    border: 'tw-border-connx-success',
    borderLeft: 'tw-border-l-connx-success',
    text: 'tw-text-connx-success',
    bg: 'tw-bg-connx-success',
  },
  warning: {
    border: 'tw-border-connx-warning',
    borderLeft: 'tw-border-l-connx-warning',
    text: 'tw-text-connx-warning',
    bg: 'tw-bg-connx-warning',
  },
  danger: dangerStyles,
  error: dangerStyles,
  info: {
    border: 'tw-border-connx-info',
    borderLeft: 'tw-border-l-connx-info',
    text: 'tw-text-connx-info',
    bg: 'tw-bg-connx-info',
  },
};

const statusClassesTint = {
  success: { bg: 'tw-bg-connx-success-tint-95' },
  warning: { bg: 'tw-bg-connx-warning-tint-95' },
  danger: { bg: 'tw-bg-connx-danger-tint-95' },
  error: { bg: '' },
  info: { bg: '' },
};

const percentage = computed(() =>
  props.ratio && props.ratio.total > 0 ? (props.ratio.value / props.ratio.total) * 100 : 0
);

const progressHeight = computed(() => {
  return typeof props.progressHeight === 'number'
    ? `${props.progressHeight}px`
    : props.progressHeight || '20px';
});

// default rounded to true
const isRounded = computed(() => props.progressrounded !== false);
</script>

<template>
  <div
    class="tw-border-2 tw-border-solid tw-rounded-lg tw-p-6 tw-my-1 tw-flex-1"
    :class="[statusClasses[status]?.border, statusClassesTint[status]?.bg]"
  >
    <div class="tw-grid tw-grid-cols-1 tw-items-center tw-gap-y-4 md:tw-gap-x-8 tw-h-full">
      <!-- Title -->
      <div v-if="title != null" class="tw-text-5xl tw-font-bold tw-text-center" :class="statusClasses[status]?.text">
        {{ title }}
      </div>

      <!-- Subtitle -->
      <div
        v-if="subttitle != null"
        class="tw-w-full tw-max-w-xs tw-rounded-2xl tw-items-center tw-px-4 tw-py-1 tw-text-2xl tw-text-center tw-mx-auto"
        :class="statusClasses[status]?.bg"
      >
        <div class="tw-text-connx-white">
          {{ subttitle }}
        </div>
      </div>

      <!-- Badge + Label + Progress -->
      <div
        v-if="badgeText != null || labelText != null || ratio != null"
        class="tw-flex tw-flex-col tw-items-center tw-w-full tw-gap-3 tw-pb-2"
      >
        <div class="tw-w-full tw-max-w-xs tw-flex tw-flex-col tw-items-center tw-gap-3">
          <!-- Badge -->
          <div v-if="badgeText != null" class="tw-w-full tw-rounded-2xl tw-px-4 tw-py-1" :class="statusClasses[status]?.bg">
            <div class="tw-normal-case tw-text-connx-white tw-text-center">
              {{ badgeText }}
            </div>
          </div>

          <!-- Label -->
          <div v-if="labelText != null" class="tw-w-full tw-text-center">
            {{ labelText }}
          </div>

          <!-- Progress bar -->
          <div
            v-if="ratio != null"
            class="tw-w-full tw-bg-connx-grey-tint-50 tw-overflow-hidden"
            :class="{ 'tw-rounded-full': isRounded }"
            :style="{ height: progressHeight }"
          >
            <div
              class="tw-h-full tw-transition-all tw-duration-500"
              :class="statusClasses[status]?.bg"
              :style="{ width: `${(ratio.value / ratio.total) * 100}%` }"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
